import type { ButtonHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

export type BigButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'outline-danger';

export interface BigButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BigButtonVariant;
  children: ReactNode;
}

const VARIANT_CLASSES: Record<BigButtonVariant, string> = {
  // White text on teal only qualifies as WCAG AA "large text" (>=3:1, teal
  // measures ~4.03:1) at >=18.66px *bold* (weight 700) - text-xl/font-bold
  // below clears that; the previous text-lg/font-semibold (18px/600) did
  // not. See the QA report, P3 #15.
  primary: 'bg-teal text-white',
  // Pink/orange fills always pair with dark ink text, never white.
  secondary: 'bg-pink text-ink',
  outline: 'bg-surface border-2 border-teal-dark text-teal-dark',
  danger: 'bg-danger text-white',
  // Secondary-to-Confirm "cancel sale" style: danger-coloured, but outline
  // rather than filled, so it reads as secondary next to a filled primary action.
  'outline-danger': 'bg-surface border-2 border-danger text-danger',
};

/** A big, thumb-friendly button (>=48px tall) using the app's Poppins-700 heading style. */
export function BigButton({ variant = 'primary', className, children, disabled, ...rest }: BigButtonProps) {
  return (
    <button
      type="button"
      className={clsx(
        'min-h-12 px-6 rounded-lg font-heading font-bold text-xl transition-opacity whitespace-nowrap',
        VARIANT_CLASSES[variant],
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90 active:opacity-80',
        className,
      )}
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  );
}
